import { PhpArray, type PhpKey, type PhpKeyInput, normalizeKey } from './ordered-array';
import { compare, identical, equal, strval, truthy, floatval, intval, phpCopy, PhpFloat, PHP_INT_MIN, PHP_INT_MAX } from './values';
import { phpStableSort } from '../compile/php-stable-sort';
import { strcmp, strcasecmp, strnatcmp, strnatcasecmp } from './strings';

export type PhpArrayInput<V = unknown> = PhpArray<PhpKey, V> | readonly V[] | Record<string, V>;
export const SORT_REGULAR = 0, SORT_NUMERIC = 1, SORT_STRING = 2, SORT_LOCALE_STRING = 5, SORT_NATURAL = 6, SORT_FLAG_CASE = 8;
export const ARRAY_FILTER_USE_BOTH = 1, ARRAY_FILTER_USE_KEY = 2;
/** PHP shuffle: return a separated, reindexed array for assignment to the ref.
 * PHP's descending Fisher-Yates consumes the same bounded draws as REF-R. */
export function shuffle<V>(input: PhpArrayInput<V>, env: { rand(min: number, max: number): number }): PhpArray<number, V> {
    const values = [...ordered(input).values()].map(v => phpCopy(v));
    for (let i = values.length - 1; i > 0; i--) {
        const j = env.rand(0, i);
        [values[i], values[j]] = [values[j]!, values[i]!];
    }
    return new PhpArray(values.map((v, i) => [i, v]));
}
/** PHP array_rand: single key or insertion-ordered selection of keys. */
export function array_rand(input: PhpArrayInput, n = 1, env: { rand(min: number, max: number): number }): PhpKey | PhpKey[] {
    const array = ordered(input), keys = [...array.keys()], count = keys.length;
    const fail = (message: string): never => { const error = new Error(message); error.name = 'ValueError'; throw error; };
    if (!count) fail('array_rand(): Argument #1 ($array) must not be empty');
    if (!Number.isInteger(n) || n < 1 || n > count) fail('array_rand(): Argument #2 ($num) must be between 1 and the number of elements in argument #1 ($array)');
    if (n === 1) {
        // Packed holes remain sampleable until density falls below one half.
        const slots = array.randomBucketKeys, used = slots.length;
        if (count >= used - (used >> 1)) {
            let key: PhpKey | null;
            do { key = slots[env.rand(0, used - 1)]!; } while (key === null);
            return key;
        }
        return keys[env.rand(0, count - 1)]!;
    }
    const negative = n > (count >> 1), wanted = negative ? count - n : n, selected = new Set<number>();
    let failures = 0;
    while (selected.size < wanted) {
        const index = env.rand(0, count - 1);
        if (selected.has(index)) {
            if (++failures > 50) { const error = new Error('Failed to generate an acceptable random number in 50 attempts'); error.name = 'Random\\BrokenRandomEngineError'; throw error; }
        } else { selected.add(index); failures = 0; }
    }
    return keys.filter((_, i) => selected.has(i) !== negative);
}
const ordered = <V>(input: PhpArrayInput<V>): PhpArray<PhpKey, V> => {
    if (input === null || typeof input !== 'object' || input instanceof PhpFloat) throw new TypeError('PHP array argument required');
    return input instanceof PhpArray ? input : new PhpArray(Object.entries(input).map(([key, value]) => [key, value as V]));
};
const output = (packed = true): PhpArray => new PhpArray([], packed);

/** Callback parameters are PHP values, separated only on writes. A lazy view
 * avoids deep-copying immutable payloads on every comparison/filter/map call.
 * phpCopy materializes a returned view once at the output boundary. */
function callbackCopy(value: unknown): unknown {
    if (value === null || typeof value !== 'object' || value instanceof PhpFloat) return value;
    let current = value;
    const children = new Map<PropertyKey | bigint, unknown>();
    const child = (key: PropertyKey | bigint, entry: unknown): unknown => {
        if (entry === null || typeof entry !== 'object' || entry instanceof PhpFloat) return entry;
        if (!children.has(key)) children.set(key, callbackCopy(entry));
        return children.get(key);
    };
    const separate = (): void => {
        if (current === value) current = value instanceof PhpArray ? value.copy()
            : Array.isArray(value) ? value.slice() : { ...value };
    };
    const target = value instanceof PhpArray ? new PhpArray() : Array.isArray(value) ? [] : {};
    return new Proxy(target, {
        get: (_target, key, receiver) => {
            if (current instanceof PhpArray) {
                const array = current;
                const entry = (k: PhpKeyInput): unknown => child(normalizeKey(k), array.get(k));
                if (key === 'get') return entry;
                if (key === 'size') return array.size;
                if (key === 'has' || key === 'keys') return array[key].bind(array);
                if (key === 'entries' || key === Symbol.iterator) return function* () { for (const k of array.keys()) yield [k, entry(k)]; };
                if (key === 'values') return function* () { for (const k of array.keys()) yield entry(k); };
                if (key === 'copy') return (map: (v: unknown) => unknown = v => v, order = array.keys()) => {
                    const keys = [...order]; let index = 0;
                    return array.copy(v => map(child(keys[index++]!, v)), keys);
                };
                if (key === 'set' || key === 'delete' || key === 'append' || key === 'clear') return (...args: unknown[]) => {
                    separate();
                    const mutable = current as PhpArray;
                    if (key === 'set' || key === 'delete') children.delete(normalizeKey(args[0] as PhpKeyInput));
                    if (key === 'set') { mutable.set(args[0] as PhpKeyInput, args[1]); return receiver; }
                    if (key === 'delete') return mutable.delete(args[0] as PhpKeyInput);
                    if (key === 'clear') { mutable.clear(); children.clear(); return; }
                    mutable.append(args[0]); return receiver;
                };
            }
            return child(key, Reflect.get(current, key, receiver));
        },
        set: (_target, key, entry) => { separate(); children.delete(key); return Reflect.set(current, key, entry); },
        deleteProperty: (_target, key) => { separate(); children.delete(key); return Reflect.deleteProperty(current, key); },
        defineProperty: (_target, key, descriptor) => { separate(); children.delete(key); return Reflect.defineProperty(current, key, descriptor); },
        ownKeys: () => Reflect.ownKeys(current),
        has: (_target, key) => Reflect.has(current, key),
        getOwnPropertyDescriptor: (_target, key) => {
            const descriptor = Reflect.getOwnPropertyDescriptor(current, key);
            return descriptor && { ...descriptor, configurable: !(Array.isArray(current) && key === 'length'),
                ...('value' in descriptor ? { value: child(key, descriptor.value) } : {}) };
        },
    });
}

export function array_merge(...arrays: PhpArrayInput[]): PhpArray {
    const sources = arrays.map(ordered);
    // Zend's two-array empty fast path copies a packed array without holes,
    // including its unset-tail append cursor, or an array with only string keys.
    if (sources.length === 2 && (sources[0]!.size === 0 || sources[1]!.size === 0)) {
        const kept = sources[sources[0]!.size === 0 ? 1 : 0]!;
        if (kept.isPacked && array_is_list(kept) || [...kept.keys()].every(key => typeof key === 'string')) return phpCopy(kept);
    }
    // Zend initializes the slow-path result from the first input's storage;
    // an empty first array uses hash storage even when later keys form a list.
    const result = output(Boolean(sources[0]?.size && sources[0].isPacked));
    for (const input of sources) for (const [key, value] of input) {
        if (typeof key === 'string') result.set(key, phpCopy(value)); else result.append(phpCopy(value));
    }
    return result;
}
export function array_union(left: PhpArrayInput, right: PhpArrayInput): PhpArray {
    const result = phpCopy(ordered(left));
    for (const [key, value] of ordered(right)) if (!result.has(key)) result.set(key, phpCopy(value));
    return result;
}
export function array_replace(...arrays: PhpArrayInput[]): PhpArray {
    if (!arrays.length) throw new TypeError('array_replace requires an array');
    const result = phpCopy(ordered(arrays[0]!));
    for (const input of arrays.slice(1)) for (const [key, value] of ordered(input)) result.set(key, phpCopy(value));
    return result;
}
export function array_values(input: PhpArrayInput): PhpArray {
    const result = output();
    for (const value of ordered(input).values()) result.append(phpCopy(value));
    return result;
}
export function array_keys(input: PhpArrayInput, search?: unknown, strict = false): PhpArray {
    const result = output();
    for (const [key, value] of ordered(input)) if (arguments.length < 2 || (strict ? identical(value, search) : equal(value, search))) result.append(key);
    return result;
}
export function array_filter(input: PhpArrayInput, callback?: (...values: unknown[]) => unknown, mode = 0): PhpArray {
    const result = output();
    for (const [key, value] of ordered(input)) {
        const keep = callback ? mode === ARRAY_FILTER_USE_KEY ? callback(key) : mode === ARRAY_FILTER_USE_BOTH ? callback(callbackCopy(value), key) : callback(callbackCopy(value)) : value;
        if (truthy(keep)) result.set(key, phpCopy(value));
    }
    return result;
}
export function array_map(callback: ((...values: unknown[]) => unknown) | null, ...arrays: PhpArrayInput[]): PhpArray {
    if (!arrays.length) throw new TypeError('array_map requires an array');
    const first = ordered(arrays[0]!);
    if (arrays.length === 1) {
        if (callback === null) return phpCopy(first);
        const result = output(first.isPacked);
        for (const [key, value] of first) result.set(key, phpCopy(callback(callbackCopy(value))));
        return result;
    }
    const lists = arrays.map(input => [...ordered(input).values()]), result = output();
    for (let i = 0; i < Math.max(...lists.map(list => list.length)); i++) {
        const values = lists.map(list => list[i] ?? null);
        result.append(callback ? phpCopy(callback(...values.map(callbackCopy))) : new PhpArray(values.map((v, k) => [k, phpCopy(v)])));
    }
    return result;
}
export function array_unique(input: PhpArrayInput): PhpArray {
    if (ordered(input).size <= 1) return phpCopy(ordered(input));
    const seen = new Set<string>(), result = output();
    for (const [key, value] of ordered(input)) {
        const text = strval(value);
        if (!seen.has(text)) { seen.add(text); result.set(key, phpCopy(value)); }
    }
    return result;
}
export function array_search(needle: unknown, input: PhpArrayInput, strict = false): PhpKey | false {
    for (const [key, value] of ordered(input)) if (strict ? identical(needle, value) : equal(needle, value)) return key;
    return false;
}
export const in_array = (needle: unknown, input: PhpArrayInput, strict = false): boolean => array_search(needle, input, strict) !== false;
export function array_slice(input: PhpArrayInput, offset: number, length: number | null = null, preserve_keys = false): PhpArray {
    const entries = [...ordered(input)], start = offset < 0 ? Math.max(0, entries.length + Math.trunc(offset)) : Math.min(entries.length, Math.trunc(offset));
    const end = length === null ? entries.length : length < 0 ? Math.max(start, entries.length + Math.trunc(length)) : start + Math.trunc(length);
    const result = output();
    for (const [key, value] of entries.slice(start, end)) {
        if (preserve_keys || typeof key === 'string') result.set(key, phpCopy(value)); else result.append(phpCopy(value));
    }
    return result;
}
export function array_column(input: PhpArrayInput, column: PhpKeyInput, index: PhpKeyInput = null): PhpArray {
    const result = output();
    for (const row of ordered(input).values()) {
        if (row === null || typeof row !== 'object' || row instanceof PhpFloat) {
            if (column === null) result.append(phpCopy(row));
            continue;
        }
        const fields = ordered(row as PhpArrayInput);
        if (column !== null && !fields.has(column)) continue;
        const value = column === null ? fields : fields.get(column);
        if (index !== null && fields.has(index)) {
            const key = fields.get(index);
            if (typeof key === 'object' && key !== null && !(key instanceof PhpFloat)) throw new TypeError('Illegal array_column index');
            result.set(key as PhpKeyInput, phpCopy(value));
        } else result.append(phpCopy(value));
    }
    return result;
}
export const array_key_first = (input: PhpArrayInput): PhpKey | null => ordered(input).keys().next().value ?? null;
export const array_key_last = (input: PhpArrayInput): PhpKey | null => [...ordered(input).keys()].pop() ?? null;
export function array_is_list(input: PhpArrayInput): boolean {
    let i = 0;
    for (const key of ordered(input).keys()) if (key !== i++) return false;
    return true;
}
export function count(input: PhpArrayInput, recursive = false): number {
    let result = ordered(input).size;
    if (recursive) for (const value of ordered(input).values()) {
        if (value !== null && typeof value === 'object' && !(value instanceof PhpFloat)) result += count(value as PhpArrayInput, true);
    }
    return result;
}
export function array_sum(input: PhpArrayInput): number | bigint | PhpFloat {
    let sum: number | bigint = 0n;
    for (const value of ordered(input).values()) {
        if (typeof value === 'object' && value !== null && !(value instanceof PhpFloat) || value == null) continue;
        const token = typeof value === 'string' ? value.match(/^[\t\n\r\v\f ]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)/i)?.[1] : undefined;
        if (typeof value === 'string' && !token) continue;
        const integer = typeof value === 'bigint' ? value : typeof value === 'boolean' ? BigInt(Number(value))
            : typeof value === 'number' && Number.isSafeInteger(value) ? BigInt(value)
                : token && /^[+-]?\d+$/.test(token) ? BigInt(token) : null;
        // Zend's negative int64 boundary falls back to double when any trailing
        // text remains after the digit scan (other in-range ints stay int).
        const minWithTail = integer === PHP_INT_MIN && typeof value === 'string' && !/^[\t\n\r\v\f ]*[+-]?\d+$/.test(value);
        if (integer !== null && !minWithTail && integer >= PHP_INT_MIN && integer <= PHP_INT_MAX && typeof sum === 'bigint') {
            const next: bigint = sum + integer;
            sum = next >= PHP_INT_MIN && next <= PHP_INT_MAX ? next : Number(next);
        } else sum = Number(sum) + Number(floatval(value));
    }
    return typeof sum === 'number' ? new PhpFloat(sum) : sum >= BigInt(Number.MIN_SAFE_INTEGER) && sum <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(sum) : sum;
}
function comparator(flags: number): (a: unknown, b: unknown) => number {
    const mode = flags & ~SORT_FLAG_CASE, insensitive = Boolean(flags & SORT_FLAG_CASE);
    if (mode === SORT_REGULAR) return compare;
    if (mode === SORT_NUMERIC) return (a, b) => Number(floatval(a)) < Number(floatval(b)) ? -1 : Number(floatval(a)) > Number(floatval(b)) ? 1 : 0;
    if (mode === SORT_STRING) return insensitive ? (a, b) => strcasecmp(strval(a), strval(b)) : (a, b) => strcmp(strval(a), strval(b));
    if (mode === SORT_NATURAL) return insensitive ? (a, b) => strnatcasecmp(strval(a), strval(b)) : (a, b) => strnatcmp(strval(a), strval(b));
    throw new RangeError('Unsupported PHP sort flag (locale-dependent sorting requires an explicit locale)');
}
/** Functional versions of PHP's by-reference sorts: assign the returned array.
 * Inputs are never mutated. PHP 8 stable ties and Zend comparison order are reused.
 */
function sorted(input: PhpArrayInput, cmp: (a: unknown, b: unknown) => number, keys: boolean, preserve: boolean, builtinKeySort = false): PhpArray {
    const source = phpCopy(ordered(input));
    // Empty sorts leave the cursor intact; only builtin key sorts also return
    // early for a singleton before converting packed storage to hash storage.
    if (source.size === 0 || builtinKeySort && source.size === 1) return source;
    const entries = phpStableSort([...source], (a, b) => cmp(a[keys ? 0 : 1], b[keys ? 0 : 1]));
    if (preserve) return source.copy(value => value, entries.map(([key]) => key), false);
    const result = output();
    for (const [, value] of entries) result.append(value);
    return result;
}
type UserComparator = (a: unknown, b: unknown) => number | boolean;
function userComparator(cmp: UserComparator): (a: unknown, b: unknown) => number {
    return (a, b) => {
        const result = cmp(callbackCopy(a), callbackCopy(b));
        // PHP 8 retries false with swapped operands before stable tie handling.
        return typeof result === 'boolean' ? result ? 1 : -Number(intval(cmp(callbackCopy(b), callbackCopy(a)))) : Number(intval(result));
    };
}
export const usort = (input: PhpArrayInput, cmp: UserComparator): PhpArray => sorted(input, userComparator(cmp), false, false);
export const uasort = (input: PhpArrayInput, cmp: UserComparator): PhpArray => sorted(input, userComparator(cmp), false, true);
export const uksort = (input: PhpArrayInput, cmp: UserComparator): PhpArray => sorted(input, userComparator(cmp), true, true);
export const sort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, comparator(flags), false, false);
export const rsort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, (a, b) => -comparator(flags)(a, b), false, false);
export const asort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, comparator(flags), false, true);
export const arsort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, (a, b) => -comparator(flags)(a, b), false, true);
export const ksort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, comparator(flags), true, true, true);
export const krsort = (input: PhpArrayInput, flags = SORT_REGULAR): PhpArray => sorted(input, (a, b) => -comparator(flags)(a, b), true, true, true);
